import { supabase } from '../supabase';
import { GameReview } from '../../types';

/**
 * Função para transformar o resultado da RPC get_game_reviews_with_votes
 * Esta função assume que a RPC retorna um objeto com os campos esperados por GameReview.
 * Ajuste os campos conforme a estrutura exata retornada pela sua RPC.
 */
const transformRawRpcReview = (rawReview: any): GameReview => {
  return {
    id: rawReview.id,
    user_id: rawReview.user_id,
    game_id: rawReview.game_id,
    review_text: rawReview.review_text,
    created_at: rawReview.created_at,
    updated_at: rawReview.updated_at,
    username: rawReview.username || '',       // Assumindo que a RPC já faz o join e retorna username
    avatar_url: rawReview.avatar_url || '',   // Assumindo que a RPC já faz o join e retorna avatar_url
    rating: rawReview.rating || null,         // Assumindo que a RPC retorna rating (da user_games)
    game_name: rawReview.game_name || '',     // Assumindo que a RPC retorna game_name
    game_slug: rawReview.game_slug || '',     // Assumindo que a RPC retorna game_slug
    game_image_url: rawReview.game_image_url || null,
    game_thumb_url: rawReview.game_thumb_url || null,
    upvotes: rawReview.upvotes || 0,
    downvotes: rawReview.downvotes || 0,
    vote_balance: rawReview.vote_balance || 0,
    user_vote: rawReview.user_vote || null,
  };
};

/**
 * Obter avaliações recentes para exibição na página de administração
 */
export const getRecentGameReviews = async (limit: number = 5): Promise<GameReview[]> => {
  try {
    const { data, error } = await supabase
      .from('game_reviews')
      .select(`
        id, 
        user_id, 
        game_id, 
        review_text, 
        created_at, 
        updated_at,
        profiles!game_reviews_user_id_fkey(username, avatar_url), 
        games!game_reviews_game_id_fkey(name, slug, image_url, thumb_url)
      `)
      .order('created_at', { ascending: false })
      .limit(limit);
    
    if (error) throw error;
    
    // Transformar os dados para seguir o formato esperado de GameReview
    const formattedReviews = data.map(review => ({
      id: review.id,
      user_id: review.user_id,
      game_id: review.game_id,
      review_text: review.review_text,
      created_at: review.created_at,
      updated_at: review.updated_at,
      username: review.profiles?.username || '', // Acesso direto ao objeto
      avatar_url: review.profiles?.avatar_url || '', // Acesso direto ao objeto
      // user_games pode ser null se for left join e não houver correspondência
      rating: undefined, // Rating de user_games não é buscado diretamente aqui agora
      game_name: review.games?.name || '', // Acesso direto ao objeto
      game_slug: review.games?.slug || '', // Acesso direto ao objeto
      game_image_url: review.games?.image_url || null,
      game_thumb_url: review.games?.thumb_url || null,
      // Campos de votos não estão nesta query, inicializar se necessário
      upvotes: 0, 
      downvotes: 0,
      vote_balance: 0,
      user_vote: null,
    }));
    
    return formattedReviews;
  } catch (error) {
    console.error('Erro ao buscar avaliações recentes:', error);
    throw error;
  }
};

/**
 * Obter todas as avaliações de um jogo
 */
export const getGameReviews = async (gameId: string): Promise<GameReview[]> => {
  try {
    const { data: rpcData, error } = await supabase
      .rpc('get_game_reviews_with_votes', { p_game_id: gameId });
    
    if (error) throw error;
    if (!rpcData) return [];

    // Transforma cada item do array Json[] para GameReview
    return rpcData.map(transformRawRpcReview);
  } catch (error) {
    console.error('Erro ao buscar avaliações do jogo:', error);
    throw error;
  }
};

/**
 * Obter uma avaliação específica pelo ID
 */
export const getReviewById = async (reviewId: string): Promise<GameReview | null> => {
  try {
    const { data, error } = await supabase
      .from('game_reviews')
      .select('*, profiles(username, avatar_url), user_games(rating)')
      .eq('id', reviewId)
      .single();
    
    if (error) throw error;
    return data as unknown as GameReview;
  } catch (error) {
    console.error('Erro ao buscar avaliação:', error);
    throw error;
  }
};

/**
 * Obter a avaliação de um usuário para um jogo específico
 */
export const getUserGameReview = async (userId: string, gameId: string): Promise<GameReview | null> => {
  try {
    // 1. Buscar a avaliação textual (game_review) e detalhes do jogo/perfil
    const { data: reviewData, error: reviewError } = await supabase
      .from('game_reviews')
      .select(`
        *,
        games!inner(name, slug, image_url, thumb_url, year, min_players, max_players),
        profiles!inner(username, avatar_url)
      `)
      .eq('user_id', userId)
      .eq('game_id', gameId)
      .single();

    // 2. Buscar o rating da user_games
    const { data: userGameEntry, error: userGameError } = await supabase
      .from('user_games')
      .select('rating')
      .eq('user_id', userId)
      .eq('game_id', gameId)
      .single();

    if (reviewError && reviewError.code !== 'PGRST116') {
      // Erro real ao buscar review, não apenas "não encontrado"
      throw reviewError;
    }
    if (userGameError && userGameError.code !== 'PGRST116') {
      // Erro real ao buscar user_game, não apenas "não encontrado"
      console.error("Erro ao buscar user_game para rating:", userGameError);
      // Prosseguir sem o rating de user_games se der erro aqui mas a review foi encontrada
    }

    const userRating = userGameEntry?.rating !== undefined ? userGameEntry.rating : null;

    if (!reviewData && !userGameEntry) {
      // Nem review textual, nem entrada em user_games
      return null;
    }

    // Construir o objeto GameReview
    // Se reviewData existe, usamos seus dados. Se não, é uma entrada "só com rating".
    const gameDetailsFromData = reviewData?.games as any;
    const profileDetailsFromData = reviewData?.profiles as any;

    return {
      id: reviewData?.id || '', // ID da review, ou vazio se só temos rating
      user_id: userId,
      game_id: gameId,
      review_text: reviewData?.review_text || '',
      created_at: reviewData?.created_at || '',
      updated_at: reviewData?.updated_at || '',
      
      // Detalhes do jogo (prioriza reviewData.games se existir)
      game_name: gameDetailsFromData?.name || 'Jogo Desconhecido',
      game_slug: gameDetailsFromData?.slug || '',
      game_image_url: gameDetailsFromData?.image_url || null,
      game_thumb_url: gameDetailsFromData?.thumb_url || null,
      game_details: {
        year: gameDetailsFromData?.year || null,
        min_players: gameDetailsFromData?.min_players || null,
        max_players: gameDetailsFromData?.max_players || null,
      },

      // Detalhes do perfil (autor da review, de reviewData.profiles)
      username: profileDetailsFromData?.username || '',
      avatar_url: profileDetailsFromData?.avatar_url || '',
      
      rating: userRating, // Rating de user_games

      // Campos de votos (não são buscados aqui, inicializar como default)
      // Estes viriam da RPC get_game_reviews_with_votes se estivéssemos listando todas as reviews
      upvotes: 0,
      downvotes: 0,
      vote_balance: 0,
      user_vote: null,
    } as GameReview; // Cast para GameReview, pois alguns campos opcionais podem estar faltando

  } catch (error) {
    console.error('Erro ao buscar avaliação do usuário para o jogo:', error);
    throw error;
  }
};

/**
 * Criar uma nova avaliação
 */
export const createGameReview = async (
  userId: string, 
  gameId: string, 
  reviewText: string
): Promise<GameReview> => {
  try {
    // Verificar se o usuário já tem uma avaliação para este jogo
    const existingReview = await getUserGameReview(userId, gameId);
    if (existingReview) {
      throw new Error('Você já avaliou este jogo. Edite sua avaliação existente.');
    }
    
    // Criar a avaliação
    const { data, error } = await supabase
      .from('game_reviews')
      .insert({
        user_id: userId,
        game_id: gameId,
        review_text: reviewText
      })
      .select()
      .single();
    
    if (error) throw error;
    
    // Buscar os dados completos da avaliação usando a RPC para incluir os dados do usuário e rating
    const { data: rpcFullReviewData, error: rpcError } = await supabase
      .rpc('get_game_reviews_with_votes', { p_game_id: gameId });
      
    if (rpcError) throw rpcError;
    
    // Encontrar a avaliação recém-criada no resultado da RPC
    const transformedFullReviewData = rpcFullReviewData ? rpcFullReviewData.map(transformRawRpcReview) : [];
    const createdReview = transformedFullReviewData.find(review => review.id === data.id);
    
    // Se não encontrar na RPC (pouco provável, mas como fallback), retornar o dado básico com um cast
    // Idealmente, a RPC sempre retornará a avaliação criada.
    return createdReview || transformRawRpcReview(data); // Transforma o 'data' básico também
  } catch (error) {
    console.error('Erro ao criar avaliação:', error);
    throw error;
  }
};

/**
 * Atualizar uma avaliação existente
 */
export const updateGameReview = async (
  reviewId: string, 
  reviewText: string,
  userId: string // Para verificação de segurança
): Promise<GameReview> => {
  try {
    // Verificar se a avaliação pertence ao usuário
    const { data: review, error: fetchError } = await supabase
      .from('game_reviews')
      .select('user_id, game_id')
      .eq('id', reviewId)
      .single();
    
    if (fetchError) throw fetchError;
    
    if (review.user_id !== userId) {
      throw new Error('Você não tem permissão para editar esta avaliação.');
    }
    
    // Atualizar a avaliação
    const { data, error } = await supabase
      .from('game_reviews')
      .update({ review_text: reviewText })
      .eq('id', reviewId)
      .select()
      .single();
    
    if (error) throw error;
    
    // Buscar os dados completos da avaliação usando a RPC
    const { data: rpcFullReviewData, error: rpcError } = await supabase
      .rpc('get_game_reviews_with_votes', { p_game_id: review.game_id });
      
    if (rpcError) throw rpcError;
    
    // Encontrar a avaliação atualizada no resultado da RPC
    const transformedFullReviewData = rpcFullReviewData ? rpcFullReviewData.map(transformRawRpcReview) : [];
    const updatedReview = transformedFullReviewData.find(r => r.id === reviewId);
    
    return updatedReview || transformRawRpcReview(data); // Transforma o 'data' básico também
  } catch (error) {
    console.error('Erro ao atualizar avaliação:', error);
    throw error;
  }
};

/**
 * Excluir uma avaliação
 */
export const deleteGameReview = async (
  reviewId: string,
  userId: string, // Para verificação de segurança
  isAdmin: boolean = false // Novo parâmetro para verificar se é admin
): Promise<void> => {
  try {
    // Se não for admin, verificar se a avaliação pertence ao usuário
    if (!isAdmin) {
      const { data: review, error: fetchError } = await supabase
        .from('game_reviews')
        .select('user_id')
        .eq('id', reviewId)
        .single();
      
      if (fetchError) throw fetchError;
      
      if (review.user_id !== userId) {
        throw new Error('Você não tem permissão para excluir esta avaliação.');
      }
    }
    
    // Excluir a avaliação
    const { error } = await supabase
      .from('game_reviews')
      .delete()
      .eq('id', reviewId);
    
    if (error) throw error;
  } catch (error) {
    console.error('Erro ao excluir avaliação:', error);
    throw error;
  }
};

/**
 * Verificar se o usuário já votou em uma avaliação específica
 */
export const getUserVoteForReview = async (userId: string, reviewId: string) => {
  try {
    const { data, error } = await supabase
      .from('review_votes')
      .select('vote_type')
      .eq('user_id', userId)
      .eq('review_id', reviewId)
      .single();
    
    if (error) {
      if (error.code === 'PGRST116') { // Nenhuma linha encontrada
        return null;
      }
      throw error;
    }
    
    return data.vote_type;
  } catch (error) {
    console.error('Erro ao verificar voto do usuário:', error);
    throw error;
  }
};

/**
 * Votar em uma avaliação (upvote/downvote)
 * @param userId ID do usuário que está votando
 * @param reviewId ID da avaliação
 * @param voteType 'upvote' ou 'downvote'
 * @returns Contagem atualizada de votos
 */
export const voteOnReview = async (
  userId: string,
  reviewId: string,
  voteType: 'upvote' | 'downvote'
) => {
  try {
    // Verificar se o usuário já votou nesta avaliação
    const existingVote = await getUserVoteForReview(userId, reviewId);
    
    // Verificar se o usuário é o autor da avaliação
    const { data: review, error: reviewError } = await supabase
      .from('game_reviews')
      .select('user_id')
      .eq('id', reviewId)
      .single();
      
    if (reviewError) throw reviewError;
    
    if (review.user_id === userId) {
      throw new Error('Você não pode votar na sua própria avaliação.');
    }
    
    // Realizar a operação adequada com base no voto existente e novo voto
    if (existingVote === voteType) {
      // Remover o voto se o usuário está votando no mesmo tipo novamente
      const { error } = await supabase
        .from('review_votes')
        .delete()
        .eq('user_id', userId)
        .eq('review_id', reviewId);
        
      if (error) throw error;
    } else if (existingVote !== null) {
      // Se já existe um voto mas de tipo diferente, atualizar o voto existente
      const { error } = await supabase
        .from('review_votes')
        .update({ vote_type: voteType })
        .eq('user_id', userId)
        .eq('review_id', reviewId);
        
      if (error) throw error;
    } else {
      // Se não existe voto, inserir um novo
      const { error } = await supabase
        .from('review_votes')
        .insert({
          user_id: userId,
          review_id: reviewId,
          vote_type: voteType
        });
        
      if (error) throw error;
    }
    
    // Buscar contagem atualizada de votos
    const { data: voteCounts, error: countError } = await supabase
      .rpc('get_review_vote_counts', { review_id_param: reviewId });
      
    if (countError) throw countError;
    
    return voteCounts;
  } catch (error) {
    console.error('Erro ao votar na avaliação:', error);
    throw error;
  }
};

/**
 * Obter todas as avaliações textuais (reviews) de um usuário, incluindo detalhes do jogo.
 */
export const getUserReviews = async (userId: string): Promise<GameReview[]> => {
  try {
    const { data: rpcData, error } = await supabase
      .rpc('get_all_reviews_for_user_with_ratings' as any, { p_user_id: userId });
    
    if (error) {
      console.error(`Erro ao chamar RPC get_all_reviews_for_user_with_ratings para ${userId}:`, error);
      throw error;
    }
    if (!rpcData || !Array.isArray(rpcData)) {
      return [];
    }

    // Mapear o resultado da RPC (que é JSON[]) para o tipo GameReview[]
    const reviews: GameReview[] = rpcData.map((rawReview: any) => ({
      id: rawReview.id,
      user_id: rawReview.user_id,
      game_id: rawReview.game_id,
      review_text: rawReview.review_text,
      created_at: rawReview.created_at,
      updated_at: rawReview.updated_at,
      rating: rawReview.rating !== undefined && rawReview.rating !== null ? Number(rawReview.rating) : undefined,
      
      // Detalhes do jogo aninhados ou diretos, conforme a RPC retorna
      game_name: rawReview.game_name,
      game_slug: rawReview.game_slug,
      game_image_url: rawReview.game_image_url,
      game_thumb_url: rawReview.game_thumb_url,
      game_details: rawReview.game_details, // A RPC já retorna um objeto para game_details

      // Campos não retornados pela RPC get_all_reviews_for_user_with_ratings
      // (username/avatar do autor da review não são necessários aqui, pois é a lista de reviews DO usuário)
      // (votos também não são relevantes para esta visualização específica)
      username: '', 
      avatar_url: '',
      upvotes: 0,
      downvotes: 0,
      vote_balance: 0,
      user_vote: null,
    }));

    return reviews;

  } catch (error) {
    // Log mais detalhado do erro
    console.error(`Falha ao buscar avaliações do usuário ${userId} via RPC:`, error);
    // Lançar o erro para que a página possa tratá-lo (ex: mostrar mensagem ao usuário)
    throw error;
  }
}; 